import { createSlice } from '@reduxjs/toolkit';

const initialFormData = {
  name: '',
  thread_access_level: 'PUBLIC',
  tags: [],
  userAccess: [],
};

const initialFiles = {
  pdfFiles: [],
  wordFiles: [],
  pptFiles: [],
  textFiles: [],
  excelFiles: [],
  csvFiles: [],
};

const initialState = {
  // Listing state
  threads: [],
  total: 0,
  page: 1,
  size: 25,
  search: '',
  filterType: 'ALL', // 'ALL' | 'PUBLIC' | 'PRIVATE'

  // View state
  showForm: false,
  showRefLibrary: false,
  // { docId, docName } when the library was opened from a document's Ref pill
  refLibDocFilter: null,
  editingThread: null,
  threadDetails: null,

  // Form state
  formData: initialFormData,
  selectedUsers: [],
  // NOTE: `files` (File objects) are NOT stored in Redux because they are
  // non-serializable. They stay in local useState inside the component.

  // ── Project → Data Manager wiring (BMS-specific) ───────
  // Set only when arriving from the project form's folder edit/create buttons.
  fromProject: false,
  returnToFabricId: null,
  pendingThreadEditId: null,
  pendingCreate: false,
};

const unstructuredThreadSlice = createSlice({
  name: 'unstructuredThread',
  initialState,
  reducers: {

    // ── Listing ────────────────────────────────────────────
    setThreads(state, action) {
      state.threads = action.payload;
    },

    setTotal(state, action) {
      state.total = action.payload;
    },

    setPage(state, action) {
      state.page = action.payload;
    },

    setSize(state, action) {
      state.size = action.payload;
    },

    setSearch(state, action) {
      state.search = action.payload;
    },

    setFilterType(state, action) {
      state.filterType = action.payload;
    },

    // ── View ───────────────────────────────────────────────
    setShowForm(state, action) {
      state.showForm = action.payload;
    },

    // Opening or leaving the library any other way shows it unfiltered.
    setShowRefLibrary(state, action) {
      state.showRefLibrary = action.payload;
      state.refLibDocFilter = null;
    },

    openRefLibraryForDoc(state, action) {
      state.showRefLibrary = true;
      state.refLibDocFilter = action.payload;
    },

    setEditingThread(state, action) {
      state.editingThread = action.payload;
    },

    setThreadDetails(state, action) {
      state.threadDetails = action.payload;
    },

    // ── Form ───────────────────────────────────────────────
    setFormData(state, action) {
      state.formData = action.payload;
    },

    setSelectedUsers(state, action) {
      state.selectedUsers = action.payload;
    },

    // ── Bulk: populate form when editing ───────────────────
    loadEditingThread(state, action) {
      const thread = action.payload;
      state.editingThread = thread;
      state.showForm = true;
      state.threadDetails = null;
      state.formData = {
        name: thread.name || '',
        thread_access_level: thread.thread_access_level || 'PUBLIC',
        tags: thread.tags || [],
        userAccess: thread.user_access || [],
      };
      state.selectedUsers =
        thread.thread_access_level === 'PRIVATE'
          ? (thread.user_access || [])
              .map(u => u.user_id)
              .filter(id => id !== thread.created_by)
          : [];
    },

    // ── Open thread details ────────────────────────────────
    openThreadDetails(state) {
      state.showForm = false;
      state.editingThread = null;
      // threadDetails will be set after API call
    },

    // ── Reset form only ────────────────────────────────────
    resetForm(state) {
      state.formData = {
        name: '',
        thread_access_level: 'PRIVATE',
        tags: ['BMS trial',
  'External clinical evidence',
  'Comparative evidence synthesis',
  'Real-world evidence',
  'Epidemiology and disease background',
  'Treatment guideline',
  'Regulatory document',
  'HTA interaction',
  'HTA appraisal',
  'Health economic evidence',
  'Pricing and commercial',
  'Strategy and value messaging',
  'Expert and patient opinion'],
        userAccess: [],
      };
      state.selectedUsers = [];
      state.editingThread = null;
      state.showForm = false;
    },

    // ── Project → Data Manager wiring ──────────────────────
    enterFolderEditFromProject(state, action) {
      const { fabricId, threadId } = action.payload || {};
      state.fromProject = true;
      state.returnToFabricId = fabricId ?? null;
      state.pendingThreadEditId = threadId ?? null;
      state.pendingCreate = false;
      state.showForm = true;
      state.showRefLibrary = false;
      state.threadDetails = null;
    },

    enterFolderCreateFromProject(state, action) {
      const { fabricId } = action.payload || {};
      state.fromProject = true;
      state.returnToFabricId = fabricId ?? null;
      state.pendingThreadEditId = null;
      state.pendingCreate = true;
      state.showForm = true;
      state.showRefLibrary = false;
      state.threadDetails = null;
    },

    clearPendingFolderAction(state) {
      state.pendingThreadEditId = null;
      state.pendingCreate = false;
    },

    clearProjectOrigin(state) {
      state.fromProject = false;
      state.returnToFabricId = null;
      state.pendingThreadEditId = null;
      state.pendingCreate = false;
    },

    // ── Full reset ─────────────────────────────────────────
    resetUnstructuredThreadState() {
      return initialState;
    },
  },
});

export const {
  setThreads,
  setTotal,
  setPage,
  setSize,
  setSearch,
  setFilterType,
  setShowForm,
  setShowRefLibrary,
  openRefLibraryForDoc,
  setEditingThread,
  setThreadDetails,
  setFormData,
  setSelectedUsers,
  loadEditingThread,
  openThreadDetails,
  resetForm,
  enterFolderEditFromProject,
  enterFolderCreateFromProject,
  clearPendingFolderAction,
  clearProjectOrigin,
  resetUnstructuredThreadState,
} = unstructuredThreadSlice.actions;

export default unstructuredThreadSlice.reducer;
